"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConnecticutRules = exports.CT_THRESHOLDS = void 0;
const browser_1 = require("encamp-prisma/generated/browser");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const _FEDERAL_1 = require("./_FEDERAL");
const _stateRules_1 = require("./_stateRules");
/**
 * Connecticut (DEEP) accumulation limits, RCSA § 22a-449(c)-100 to -102.
 *
 * Connecticut's hazardous waste program incorporates 40 CFR parts 260–262 as
 * they stood on July 1, 2000, with state modifications — so it predates the
 * 2016 Generator Improvements Rule entirely. Consequences this class
 * carries:
 *
 * - the SQG clock is 180 days with no transport >200mi extension to 270 days
 * - 40 CFR 266 subpart P not adopted, so no special handling for pharma
 *
 * - the smallest generator status keeps its pre-GIR name, Conditionally Exempt Small
 *   Quantity Generator (CESQG)
 *
 * The CESQG is functionally the federal VSQG — same generation thresholds, same
 * on-site accumulation thresholds — which is why RCRAInfo offers Connecticut
 * only a "same as federal" state status (unlike Maine or Washington, whose
 * statuses differ in substance). So the state axis is ignored: every threshold
 * keys on the federal status, and the CESQG differs from a VSQG only in what the
 * threshold judgment is called.
 * SME-confirmed reading, 2026-09-16.
 */
const CT_CESQG_CITATION = "RCSA § 22a-449(c)-101 (40 CFR 261.5, July 1, 2000 ed.)";
const CT_SATELLITE_CITATION = "RCSA § 22a-449(c)-102 (40 CFR 262.34(c), July 1, 2000 ed.)";
/** Connecticut's pre-GIR name for the generator status the federal VSQG resolves to. */
const CT_CESQG_READABLE_STATUS = {
    abbreviation: "CESQG",
    name: "Conditionally Exempt Small Quantity Generator",
};
const CT_CESQG_SOURCE = { jurisdiction: "CT", citation: CT_CESQG_CITATION };
const CT_SATELLITE_SOURCE = {
    jurisdiction: "CT",
    citation: CT_SATELLITE_CITATION,
};
/** Federal rules as Connecticut incorporates them. */
exports.CT_THRESHOLDS = {
    centralTimeLimit: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.centralTimeLimit,
        source: { jurisdiction: "CT", citation: "RCSA § 22a-449(c)-101, -102" },
    },
    capNonAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capNonAcuteVolume, source: CT_SATELLITE_SOURCE },
    capAcuteVolume: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteVolume, source: CT_SATELLITE_SOURCE },
    capAcuteMass: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.capAcuteMass, source: CT_SATELLITE_SOURCE },
    satelliteToCentralMoveWindow: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.satelliteToCentralMoveWindow,
        source: CT_SATELLITE_SOURCE,
    },
    onSiteTotal: {
        ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteTotal,
        source: {
            jurisdiction: "CT",
            citation: "RCSA § 22a-449(c)-102 (40 CFR 262.34(d), July 1, 2000 ed.)",
        },
    },
    onSiteNonAcute: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteNonAcute, source: CT_CESQG_SOURCE },
    onSiteAcute: { ..._FEDERAL_1.FEDERAL_THRESHOLDS.onSiteAcute, source: CT_CESQG_SOURCE },
};
/** Connecticut (DEEP) accumulation limits, RCSA § 22a-449(c)-100 to -102. */
class ConnecticutRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.stateStatusDomain = [];
        this.adoptsEpisodicEvents = false;
    }
    /** 180 days for an SQG on both values of the 200-mile flag. */
    centralTimeLimit(situation) {
        const status = situation.facility.federalGeneratorStatus;
        switch (status) {
            case undefined:
                return {
                    ...exports.CT_THRESHOLDS.centralTimeLimit,
                    determination: "UNDETERMINED",
                    missing: ["facility.federalGeneratorStatus"],
                    basis: {},
                };
            case "VSQG":
                return {
                    ...exports.CT_THRESHOLDS.centralTimeLimit,
                    source: CT_CESQG_SOURCE,
                    determination: "EXEMPT",
                    basis: { facility: { federalGeneratorStatus: status } },
                };
            case "LQG":
            case "SQG": {
                const source = {
                    jurisdiction: "CT",
                    citation: status === "LQG"
                        ? "RCSA § 22a-449(c)-102 (40 CFR 262.34(a), July 1, 2000 ed.)"
                        : "RCSA § 22a-449(c)-102 (40 CFR 262.34(d), July 1, 2000 ed.)",
                };
                return {
                    ...exports.CT_THRESHOLDS.centralTimeLimit,
                    source,
                    determination: "KNOWN",
                    basis: { facility: { federalGeneratorStatus: status } },
                    value: status === "LQG" ? 90 : 180,
                };
            }
        }
    }
    /** Connecticut never adopted subpart L, so no container takes the episodic window. */
    containerThresholds(situation) {
        const program = this.governingProgram(situation.container);
        // A satellite container's move window reports a missing program itself.
        if (program.value === undefined && situation.area.type !== "SATELLITE") {
            const missing = [
                ...program.missing,
                ...(situation.area.type === undefined ? ["area.type"] : []),
            ];
            return [
                { ...exports.CT_THRESHOLDS.centralTimeLimit, determination: "UNDETERMINED", missing, basis: {} },
            ];
        }
        if (program.value !== undefined && _FEDERAL_1.Federal.isSubclassification(program.value)) {
            return [_FEDERAL_1.Federal.subclassificationTimeLimits[program.value]];
        }
        switch (situation.area.type) {
            case "CENTRAL":
                return [this.centralTimeLimit(situation)];
            case "SATELLITE": {
                const caps = this.satelliteCaps(situation);
                return [
                    ..._FEDERAL_1.Federal.satelliteMoveWindow(this, exports.CT_THRESHOLDS.satelliteToCentralMoveWindow, caps, situation.container),
                ];
            }
            case undefined:
                return [
                    {
                        ...exports.CT_THRESHOLDS.centralTimeLimit,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * Connecticut never adopted 40 CFR 266 subpart P, so pharmaceutical waste is
     * ordinary hazardous waste whatever the facility's subpart P flag says.
     * Universal waste and used oil are the federal programs.
     */
    governingProgram(container) {
        if (container.wasteKind === "PHARMACEUTICAL") {
            return { value: "RCRA" };
        }
        return _FEDERAL_1.Federal.governingProgram(container);
    }
    /**
     * The caps, which apply simultaneously; crossing one starts its move
     * window. An unknown status still gets them, the more protective default.
     */
    satelliteCaps(situation) {
        const status = situation.facility.federalGeneratorStatus;
        const basis = status === undefined ? {} : { facility: { federalGeneratorStatus: status } };
        if (status === "VSQG") {
            return [
                {
                    ...exports.CT_THRESHOLDS.capNonAcuteVolume,
                    source: CT_CESQG_SOURCE,
                    determination: "EXEMPT",
                    basis,
                },
                {
                    ...exports.CT_THRESHOLDS.capAcuteVolume,
                    source: CT_CESQG_SOURCE,
                    determination: "EXEMPT",
                    basis,
                },
                {
                    ...exports.CT_THRESHOLDS.capAcuteMass,
                    source: CT_CESQG_SOURCE,
                    determination: "EXEMPT",
                    basis,
                },
            ];
        }
        return [
            {
                ...exports.CT_THRESHOLDS.capNonAcuteVolume,
                determination: "KNOWN",
                basis,
            },
            {
                ...exports.CT_THRESHOLDS.capAcuteVolume,
                determination: "KNOWN",
                basis,
            },
            {
                ...exports.CT_THRESHOLDS.capAcuteMass,
                determination: "KNOWN",
                basis,
            },
        ];
    }
    areaThresholds(situation) {
        switch (situation.area.type) {
            case "CENTRAL":
                return [];
            case "SATELLITE":
                return this.satelliteCaps(situation);
            case undefined:
                return [
                    {
                        ...exports.CT_THRESHOLDS.capNonAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.CT_THRESHOLDS.capAcuteVolume,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                    {
                        ...exports.CT_THRESHOLDS.capAcuteMass,
                        determination: "UNDETERMINED",
                        missing: ["area.type"],
                        basis: {},
                    },
                ];
        }
    }
    /**
     * RCSA § 22a-449(c)-102 holds an SQG to 1,000 kg on site, a single total
     * across acute and non-acute waste. A CESQG is held to the federal VSQG
     * figures under 40 CFR 261.5 as incorporated. No crossing changes the
     * generator's status in the RCSA text, though DEEP's guidance calls a CESQG
     * over 1,000 kg one "operating as a Large Quantity Generator". Known gaps: an
     * SQG over its cap is a storage facility operator (262.34(f) as modified),
     * and a CESQG's crossing puts that waste under SQG or LQG standards on a
     * clock from the crossing (261.5(g)(2), (f)(2)). Used oil and universal
     * waste count toward none of them (261.5(c)(4), (6) as incorporated).
     */
    facilityThresholds(facility) {
        const status = facility.federalGeneratorStatus;
        switch (status) {
            case undefined:
                return [
                    {
                        ...exports.CT_THRESHOLDS.onSiteTotal,
                        source: { jurisdiction: "CT", citation: "RCSA § 22a-449(c)-101, -102" },
                        determination: "UNDETERMINED",
                        missing: ["facility.federalGeneratorStatus"],
                        basis: {},
                        consequences: [{ kind: "COMPLIANCE_NOT_RECORDED" }],
                    },
                ];
            case "LQG":
                return [];
            case "SQG":
                return [
                    {
                        ...exports.CT_THRESHOLDS.onSiteTotal,
                        determination: "KNOWN",
                        basis: { facility: { federalGeneratorStatus: status } },
                        value: wasteGeneratorStatusThresholds_1.CT_SQG_ACCUMULATION_THRESHOLD_KG,
                    },
                ];
            case "VSQG":
                return [
                    {
                        ...exports.CT_THRESHOLDS.onSiteNonAcute,
                        determination: "KNOWN",
                        basis: { facility: { federalGeneratorStatus: status } },
                        value: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_KG,
                    },
                    {
                        ...exports.CT_THRESHOLDS.onSiteAcute,
                        determination: "KNOWN",
                        basis: { facility: { federalGeneratorStatus: status } },
                        value: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_ACUTE_KG,
                    },
                ];
        }
    }
    accumulationCategory(container) {
        // STATE_REGULATED is non-acute pending the waste team's per-state decision.
        return container.classification === browser_1.WasteClassification.STATE_REGULATED
            ? { value: "NON_ACUTE" }
            : _FEDERAL_1.Federal.category(container);
    }
    /** The federal names, save the CESQG Connecticut still calls the federal VSQG. */
    describeGeneratorStatus(facility) {
        const status = facility.federalGeneratorStatus;
        if (status === undefined) {
            return null;
        }
        return status === "VSQG"
            ? CT_CESQG_READABLE_STATUS
            : _FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES[status];
    }
}
exports.ConnecticutRules = ConnecticutRules;
//# sourceMappingURL=CT.js.map